/**
 * Plugin to handle module access restrictions
 * Shows toast notifications when users are redirected due to missing module access
 */
export default defineNuxtPlugin(() => {
  const route = useRoute()
  const router = useRouter()
  const toast = useToast()
  const { isAdmin } = useUser()

  // Safe translation helper that falls back to default if i18n not ready
  const safeT = (key: string, params?: Record<string, unknown>) => {
    try {
      const { t } = useI18n()
      return t(key, params as Record<string, string>) || null
    } catch {
      return null
    }
  }

  // Module display names
  const moduleNames: Record<string, string> = {
    healthcare: 'Healthcare',
    calls: 'Calls',
    analytics: 'Analytics'
  }

  // Watch for module_required query parameter
  watch(
    () => route.query.module_required,
    (moduleKey) => {
      if (moduleKey && typeof moduleKey === 'string') {
        const moduleName = moduleNames[moduleKey] || moduleKey

        toast.add({
          id: 'module-required',
          title: safeT('module-access.feature-locked') || 'Feature Not Available',
          description: isAdmin.value
            ? safeT('module-access.enable-in-settings', { module: moduleName }) ||
              `The ${moduleName} module is not enabled. Enable it in Settings > Modules.`
            : safeT('module-access.contact-admin', { module: moduleName }) ||
              `The ${moduleName} module is required to access this feature. Contact your administrator.`,
          icon: 'i-lucide-lock',
          color: 'warning',
          timeout: 8000,
          actions: isAdmin.value
            ? [
                {
                  label: safeT('module-access.go-to-settings') || 'Go to Settings',
                  click: () => router.push('/settings')
                }
              ]
            : undefined
        })

        // Clean up the URL by removing the query param
        const query = { ...route.query }
        delete query.module_required
        router.replace({ path: route.path, query })
      }
    },
    { immediate: true }
  )
})
